// Arabic strings for retroactive data minimisation, the collection-matrix publish confirmation and the
// configurable privacy values — DEVELOPMENT DRAFT by Claude (PD-011); unreviewed until decision H-10.
// English source: en-priv.ts.
import type { enPriv } from './en-priv.ts';
import type { Message } from './types.ts';

export const arPriv: { readonly [K in keyof typeof enPriv]: Message } = {
  // ---------------------------------------------------------------- API messages
  'priv.api.impactChanged': 'تغيّرت السجلات المتأثرة بعد عرض التأكيد. راجع الأثر مرة أخرى ثم انشر.',
  'priv.api.basisNeedsNotice': 'بعض الأغراض التي تستند إلى هذا الأساس ليس لها إشعار موافقة. أضف إشعاراً لكل منها قبل أن يتطلب هذا الأساس الموافقة.',
  'priv.api.basisInUse': 'ما زالت أغراض تستند إلى هذا الأساس. غيّر تلك الأغراض أو أوقفها أولاً.',
  'priv.v.consentBasis': 'أساس الموافقة يتطلب دائماً إشعار موافقة.',
  'priv.v.basisUnknown': 'اختر أساساً قانونياً نشطاً من القائمة.',

  // ---------------------------------------------------------------- publish confirmation (B-31)
  'priv.pub.title': 'نشر الإصدار {n} من قواعد الجمع؟',
  'priv.pub.subtitle': 'تحقق مما يتغير للزوار قبل أن يسري.',
  'priv.pub.body': 'يغيّر النشر ما يمكن أن يُطلب من الزوار من الآن فصاعداً. القيم التي جُمعت سابقاً ولا تسمح بها القواعد الجديدة تُخفى فوراً في كل مكان — الشاشات وعمليات البحث والتقارير والتصدير. تعود القيم المخفية إذا سمح بها إصدار لاحق، ما لم تكن قد حُذفت.',
  'priv.pub.types': 'أنواع الزوار المتأثرة',
  'priv.pub.noTypes': 'لا تغيير لأي نوع زائر.',
  'priv.pub.items': 'الحقول والمستندات المتأثرة',
  'priv.pub.change': 'التغيير',
  'priv.pub.noChanges': 'لا تغيير في الحقول لأي نوع زائر.',
  'priv.pub.records': 'السجلات الموجودة',
  'priv.pub.hide': { zero: 'لن تُخفى أي قيمة موجودة', one: 'ستُخفى قيمة موجودة واحدة', two: 'ستُخفى قيمتان موجودتان', few: 'ستُخفى {count} قيم موجودة', many: 'ستُخفى {count} قيمة موجودة', other: 'ستُخفى {count} قيمة موجودة' },
  'priv.pub.hideHow': 'تُسحب ملفات تصدير التقارير والتدقيق وصادرات أصحاب البيانات المُعدّة قبل الآن. ويمكن لمسؤول الخصوصية بعد ذلك التخطيط لحذف مُراجَع للقيم المخفية.',
  'priv.pub.hideNone': 'لا تُخفى أي قيمة موجودة.',
  'priv.pub.restore': { zero: 'لن تظهر أي قيمة مخفية من جديد.', one: 'ستظهر من جديد قيمة واحدة أُخفيت سابقاً.', two: 'ستظهر من جديد قيمتان أُخفيتا سابقاً.', few: 'ستظهر من جديد {count} قيم أُخفيت سابقاً.', many: 'ستظهر من جديد {count} قيمة أُخفيت سابقاً.', other: 'ستظهر من جديد {count} قيمة أُخفيت سابقاً.' },
  'priv.pub.ack': { zero: 'أفهم أنه لن تُخفى أي قيمة موجودة.', one: 'أفهم أن قيمة موجودة واحدة ستُخفى.', two: 'أفهم أن قيمتين موجودتين ستُخفيان.', few: 'أفهم أن {count} قيم موجودة ستُخفى.', many: 'أفهم أن {count} قيمة موجودة ستُخفى.', other: 'أفهم أن {count} قيمة موجودة ستُخفى.' },

  // ---------------------------------------------------------------- hidden values (B-17)
  'priv.hid.title': 'القيم المخفية بموجب قواعد الجمع',
  'priv.hid.intro': 'قيم جُمعت قبل أن تمنعها قاعدة. لا يمكن لأحد رؤيتها؛ وتُحفظ فقط إلى أن يتم حذف مُراجَع أو إلى أن تسمح بها القاعدة من جديد.',
  'priv.hid.empty': 'لا توجد قيم مخفية.',
  'priv.hid.total': { zero: 'لا قيم مخفية', one: 'قيمة مخفية واحدة', two: 'قيمتان مخفيتان', few: '{count} قيم مخفية', many: '{count} قيمة مخفية', other: '{count} قيمة مخفية' },
  'priv.hid.record': 'السجل',
  'priv.hid.since': 'مخفية منذ',
  'priv.hid.restorable': 'إذا خُففت القاعدة',
  'priv.hid.byVersion': 'بموجب إصدار القواعد {n}',
  'priv.hid.canReturn': 'تعود',
  'priv.hid.newerValue': 'جُمعت قيمة أحدث',
  'priv.hid.more': 'تُعرض أول 200 فقط.',
  'priv.hid.purgeItem': 'القيم المراد حذفها',
  'priv.hid.allItems': 'كل القيم المخفية',
  'priv.hid.plan': 'التخطيط لحذف مُراجَع',
  'priv.hid.planned': 'تم التخطيط للحذف. تراجعه سلطة الخصوصية وتطبّقه من قسم مهام الحذف.',
  'priv.hid.fourEyes': 'لا يمكن لمن خطط للحذف أن يطبقه. ولا يمكن التراجع عن تطبيقه.',
  'priv.target.person_email': 'البريد الإلكتروني في الملف الشخصي',
  'priv.target.person_phone': 'رقم الهاتف المتحرك في الملف الشخصي',
  'priv.target.person_organisation': 'الجهة في الملف الشخصي',
  'priv.target.participation_organisation': 'الجهة في زيارة',
  'priv.target.profile_field': 'حقل الملف الشخصي',
  'priv.target.identity_number': 'رقم وثيقة الهوية',
  'priv.target.identity_image': 'صورة وثيقة الهوية',
  'priv.target.visitor_document': 'مستند التسجيل المسبق',
  'priv.run.field': 'الحقل: {field}',
  'pv.pg.kind.minimisation': 'حذف القيم المخفية',
  'pv.exception.no_longer_hidden': 'أُظهرت من جديد بقواعد لاحقة',

  // ---------------------------------------------------------------- legal bases (B-14)
  'priv.lb.title': 'الأسس القانونية',
  'priv.lb.intro': 'الأسس المشروعة التي يمكن أن تستند إليها الأغراض، بصياغة مؤسستك باللغتين الإنجليزية والعربية. يُحفظ كل تغيير كإصدار.',
  'priv.lb.add': 'إضافة أساس قانوني',
  'priv.lb.wording': 'الصياغة',
  'priv.lb.consent': 'يتطلب موافقة',
  'priv.lb.noWording': 'لم تُكتب بعد',
  'priv.lb.active': 'نشط',
  'priv.lb.retired': 'موقوف',
  'priv.lb.version': 'الإصدار {n}',
  'priv.lb.usedBy': { zero: 'لا يستند إليه أي غرض', one: 'يستند إليه غرض واحد', two: 'يستند إليه غرضان', few: 'يستند إليه {count} أغراض', many: 'يستند إليه {count} غرضاً', other: 'يستند إليه {count} غرض' },
  'priv.lb.edit': 'تعديل',
  'priv.lb.retire': 'إيقاف',
  'priv.lb.noInvent': 'يوفّر Passage أسساً مؤقتة فقط. تأتي الأسس وصياغتها من مراجعتكم القانونية؛ ولا شيء هنا يدّعي الامتثال.',
  'priv.lb.editTitle': 'الأساس القانوني {code}',
  'priv.lb.dialogHint': 'يُنشئ الحفظ إصداراً جديداً. تبقى الأغراض مستندة إلى الأساس برمزه.',
  'priv.lb.code': 'الرمز',
  'priv.lb.codeHint': 'أحرف لاتينية صغيرة وشرطات سفلية، مثل statutory_duty.',
  'priv.lb.labelEn': 'الاسم (بالإنجليزية)',
  'priv.lb.labelAr': 'الاسم (بالعربية)',
  'priv.lb.wordingEn': 'الصياغة القانونية (بالإنجليزية)',
  'priv.lb.wordingAr': 'الصياغة القانونية (بالعربية)',
  'priv.lb.noteHint': 'اختياري: سبب التغيير، مثل مرجع المراجعة.',
  'priv.lb.requiresConsent': 'المعالجة على هذا الأساس تتطلب إشعار موافقة مسجلاً',
  'priv.lb.retireTitle': 'إيقاف الأساس القانوني {code}؟',
  'priv.lb.retireBody': 'لا يستند إليه أي غرض. يبقى في السجل ويمكن حفظه من جديد لاحقاً.',

  // ---------------------------------------------------------------- settings (B-14)
  'priv.set.intro': 'تبيّن كل قيمة ما إذا كانت لا تزال القيمة الافتراضية الهندسية. القيم القانونية قرار للمالك؛ والحفظ يغيّر القيم التي عدّلتها فقط.',
  'priv.set.organisation': 'حددتها مؤسستك',
  'priv.set.reset': 'استخدام القيمة الافتراضية الهندسية',
  'priv.set.reportExportHours': 'مدة إتاحة تنزيل صادرات التقارير والتدقيق (بالساعات)',
  'priv.set.reportExportHint': 'فارغ: القيمة الافتراضية للتثبيت وهي {hours} ساعة.',
  'priv.set.saveHint': 'تُحفظ القيم المعدّلة فقط.',
};
